import createClient, { type Middleware } from "openapi-fetch";
import type { paths } from "./generated/schema";
import { useAuthV2 } from "@/lib/authv2/store";
import { ensureFreshSessionForRequest, refreshOn401 } from "@/lib/authv2/refreshInterceptor";
import { notePersonaHeader } from "@/lib/authv2/persona";
import { signOutIfStaffInactive } from "@/lib/authv2/staffInactive";
import { shouldSendBearer } from "./authed-fetch";
import { isNetworkFailure, NETWORK_ERROR_MESSAGE } from "./error-message";
import { checkinSessionEpoch } from "@/features/checkin/checkin-attempt";

const baseUrl = import.meta.env.VITE_API_URL ?? "http://localhost:8080/api/v1";

// Base URL for hand-built hrefs that must hit the BE directly (e.g. the
// raw-link redirect in lib/api/rawLink.ts). Same value the client uses.
export const apiBaseUrl = baseUrl;

// `credentials: 'include'` so the BE-issued session_id cookie travels with
// every business-API call (the refresh interceptor needs it to actually
// refresh).
export const api = createClient<paths>({ baseUrl, credentials: "include" });

// Capture POST/PUT/PATCH bodies at onRequest time so the 401-retry path can
// resend them. By the time onResponse fires, the original Request stream has
// been consumed by `fetch` and `request.body` is unusable. Keyed by the
// Request instance via WeakMap so entries GC when the request object is freed.
const bodyCache = new WeakMap<Request, ArrayBuffer>();
const checkinOwners = new WeakMap<Request, { sub: string | null; epoch: number }>();

const authMiddleware: Middleware = {
  async onRequest({ request }) {
    const isCheckin = request.method === "POST" && new URL(request.url).pathname.endsWith("/staff-checkins");
    const owner = isCheckin
      ? { sub: useAuthV2.getState().casdoorSub, epoch: checkinSessionEpoch() }
      : null;
    await ensureFreshSessionForRequest();
    if (owner && (owner.sub !== useAuthV2.getState().casdoorSub || owner.epoch !== checkinSessionEpoch())) {
      throw new Error("Phiên đăng nhập đã thay đổi — hãy thử check-in lại");
    }
    const { accessToken } = useAuthV2.getState();
    if (accessToken && shouldSendBearer()) request.headers.set("Authorization", `Bearer ${accessToken}`);
    if (owner) checkinOwners.set(request, owner);
    if (request.body) {
      // Clone before consumption — the original Request is what gets sent.
      const buf = await request.clone().arrayBuffer();
      bodyCache.set(request, buf);
    }
    if (owner && (owner.sub !== useAuthV2.getState().casdoorSub || owner.epoch !== checkinSessionEpoch())) {
      throw new Error("Phiên đăng nhập đã thay đổi — hãy thử check-in lại");
    }
    return request;
  },
  async onResponse({ request, response }) {
    // QĐ 0255: phản hồi nói nó được phục vụ bằng danh phận nào; lệch với màn
    // đang vẽ thì báo rồi tải lại (xem lib/authv2/persona.ts).
    notePersonaHeader(response);
    // QĐ 0412: nhân sự đã nghỉ ⇒ xoá phiên ngay, trang đăng nhập nói lý do.
    await signOutIfStaffInactive(response);
    if (response.status !== 401) return response;
    return refreshOn401(response, async () => {
      const owner = checkinOwners.get(request);
      if (owner && (owner.sub !== useAuthV2.getState().casdoorSub || owner.epoch !== checkinSessionEpoch())) {
        return response;
      }
      // Re-issue the original request with the rotated access token AND the
      // captured body bytes (request.body has already been consumed by the
      // initial fetch — see bodyCache above).
      const { accessToken } = useAuthV2.getState();
      const headers = new Headers(request.headers);
      if (accessToken && shouldSendBearer()) {
        headers.set("Authorization", `Bearer ${accessToken}`);
      } else {
        headers.delete("Authorization");
      }
      const cachedBody = bodyCache.get(request);
      return fetch(request.url, {
        method: request.method,
        headers,
        body: cachedBody ?? null,
        credentials: "include",
      });
    });
  },
};

// When the server cannot be reached at all, fetch rejects with a TypeError the
// browser words in English ("Failed to fetch"), and every screen that shows
// `e.message` put that on a toast. Swap it for a sentence the user can act on;
// the original stays as `cause`. Aborts (query cancellation) pass through.
const networkErrorMiddleware: Middleware = {
  onError({ error }) {
    if (isNetworkFailure(error)) return new Error(NETWORK_ERROR_MESSAGE, { cause: error });
  },
};

api.use(authMiddleware, networkErrorMiddleware);
